import type {
  VisualDxComparePayload,
  VisualDxDetailResult,
  VisualDxDiagnosis,
  VisualDxDrugReactionPayload,
  VisualDxGalleryPayload,
  VisualDxRankedDifferentialEntry,
} from '../../contracts';
import type { VisualDxDiagnosticFlowSelections } from '../interactive-widget/submission';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const nullableString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

export const httpsUrl = (value: unknown): string | null =>
  typeof value === 'string' && value.startsWith('https://') ? value : null;

/** HTTPS deep links plus raster VisualDx MCP ``data:image/...;base64`` thumbs. */
export const mediaUrl = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  if (value.startsWith('https://')) return value;
  if (
    /^data:image\/(?:png|jpe?g|gif|webp);base64,/i.test(value.slice(0, 64)) &&
    value.includes(';base64,')
  ) {
    return value;
  }
  return null;
};

const REGION_IDS = new Set([
  'face',
  'scalp',
  'neck',
  'chest',
  'trunk',
  'arm',
  'forearm',
  'hand',
  'leg',
  'foot',
  'genitals',
  'other',
]);
const MORPHOLOGY_IDS = new Set([
  'widespreadItching',
  'urticarialWheals',
  'erythematousMacules',
  'scalyPlaques',
  'vesiclesBullae',
  'nocturnalItchWorsens',
  'newMedicationLast2Weeks',
  'palpablePurpura',
]);
const AGE_BAND_IDS = new Set(['infant', 'child', 'adolescent', 'adult', 'olderAdult']);
const ONSET_IDS = new Set([
  'rapidly',
  'acutely',
  'chronically',
  'recurring',
  'presentAtBirth',
]);

const isValidAge = (value: unknown): boolean => {
  if (value === null) return true;
  if (!isRecord(value)) return false;
  if (
    typeof value.band === 'string' &&
    AGE_BAND_IDS.has(value.band) &&
    Object.keys(value).length === 1
  ) {
    return true;
  }
  return (
    typeof value.exactYears === 'number' &&
    Number.isInteger(value.exactYears) &&
    value.exactYears >= 0 &&
    value.exactYears <= 130 &&
    Object.keys(value).length === 1
  );
};

export const readDetail = (
  data: Record<string, unknown> | null | undefined
): VisualDxDetailResult | undefined => {
  const detail = isRecord(data?.detail) ? data.detail : null;
  if (!detail || typeof detail.name !== 'string') return undefined;
  return {
    name: detail.name,
    synopsis: nullableString(detail.synopsis),
    image_urls: Array.isArray(detail.image_urls)
      ? detail.image_urls.flatMap((url) => {
          const safeUrl = httpsUrl(url);
          return safeUrl ? [safeUrl] : [];
        })
      : [],
    visualdx_url: httpsUrl(detail.visualdx_url),
  };
};

export const readDiagnoses = (
  data: Record<string, unknown> | null | undefined
): VisualDxDiagnosis[] => {
  if (!Array.isArray(data?.diagnoses)) return [];
  return data.diagnoses.flatMap((entry): VisualDxDiagnosis[] => {
    if (!isRecord(entry) || typeof entry.name !== 'string') return [];
    return [
      {
        name: entry.name,
        thumbnail_url: httpsUrl(entry.thumbnail_url),
        visualdx_url: httpsUrl(entry.visualdx_url),
      },
    ];
  });
};

export const readDifferential = (
  data: Record<string, unknown> | null | undefined
): VisualDxRankedDifferentialEntry[] => {
  if (!Array.isArray(data?.differential)) return [];
  return data.differential.flatMap((entry): VisualDxRankedDifferentialEntry[] => {
    if (
      !isRecord(entry) ||
      typeof entry.rank !== 'number' ||
      typeof entry.name !== 'string' ||
      typeof entry.description !== 'string' ||
      typeof entry.confidence_pct !== 'number' ||
      !['routine', 'moderate', 'urgent'].includes(String(entry.urgency))
    ) {
      return [];
    }
    return [
      {
        rank: entry.rank,
        name: entry.name,
        description: entry.description,
        confidence_pct: entry.confidence_pct,
        urgency: entry.urgency as VisualDxRankedDifferentialEntry['urgency'],
        image_url: httpsUrl(entry.image_url),
        visualdx_url: httpsUrl(entry.visualdx_url),
      },
    ];
  });
};

export const readGallery = (
  data: Record<string, unknown> | null | undefined
): VisualDxGalleryPayload | undefined => {
  const gallery = isRecord(data?.gallery) ? data.gallery : null;
  if (
    !gallery ||
    typeof gallery.diagnosis !== 'string' ||
    !Array.isArray(gallery.images)
  ) {
    return undefined;
  }
  const images = gallery.images.flatMap((entry) => {
    if (!isRecord(entry)) return [];
    const skinType = entry.skin_type;
    return [
      {
        thumbnail_url: mediaUrl(entry.thumbnail_url),
        caption: nullableString(entry.caption),
        skin_type:
          typeof skinType === 'number' && skinType >= 1 && skinType <= 6
            ? skinType
            : null,
        body_location: nullableString(entry.body_location),
        visualdx_url: httpsUrl(entry.visualdx_url),
      },
    ];
  });
  return {
    diagnosis: gallery.diagnosis,
    images,
    total_images:
      typeof gallery.total_images === 'number' ? gallery.total_images : images.length,
    page: typeof gallery.page === 'number' && gallery.page >= 1 ? gallery.page : 1,
    total_pages:
      typeof gallery.total_pages === 'number' && gallery.total_pages >= 1
        ? gallery.total_pages
        : 1,
    visualdx_url: httpsUrl(gallery.visualdx_url),
  };
};

export const readCompare = (
  data: Record<string, unknown> | null | undefined
): VisualDxComparePayload | undefined => {
  const compare = isRecord(data?.compare) ? data.compare : null;
  if (!compare || !Array.isArray(compare.diagnoses)) return undefined;
  const diagnoses = compare.diagnoses.flatMap((entry) => {
    if (!isRecord(entry) || typeof entry.name !== 'string') return [];
    return [
      {
        name: entry.name,
        image_url: mediaUrl(entry.image_url),
        synopsis: nullableString(entry.synopsis),
        visualdx_url: httpsUrl(entry.visualdx_url),
      },
    ];
  });
  if (diagnoses.length < 2) return undefined;
  return {
    diagnoses,
    compare_url: httpsUrl(compare.compare_url),
  };
};

export const readDrugReaction = (
  data: Record<string, unknown> | null | undefined
): VisualDxDrugReactionPayload | undefined => {
  const drugReaction = isRecord(data?.drugReaction) ? data.drugReaction : null;
  if (
    !drugReaction ||
    typeof drugReaction.drug !== 'string' ||
    !Array.isArray(drugReaction.diagnoses)
  ) {
    return undefined;
  }
  return {
    drug: drugReaction.drug,
    diagnoses: drugReaction.diagnoses.flatMap((entry) => {
      if (!isRecord(entry) || typeof entry.name !== 'string') return [];
      const matchPct = entry.match_pct;
      return [
        {
          name: entry.name,
          thumbnail_url: mediaUrl(entry.thumbnail_url),
          match_pct:
            typeof matchPct === 'number' && Number.isFinite(matchPct)
              ? Math.max(0, Math.min(100, Math.round(matchPct)))
              : null,
          visualdx_url: httpsUrl(entry.visualdx_url),
        },
      ];
    }),
    visualdx_url: httpsUrl(drugReaction.visualdx_url),
  };
};

export const readSelections = (
  data: Record<string, unknown> | null | undefined
): VisualDxDiagnosticFlowSelections | undefined => {
  const selections = isRecord(data?.selections) ? data.selections : null;
  if (
    !selections ||
    !['front', 'back'].includes(String(selections.bodyAspect)) ||
    !Array.isArray(selections.regions) ||
    !selections.regions.every(
      (region) => typeof region === 'string' && REGION_IDS.has(region)
    ) ||
    !Array.isArray(selections.morphology) ||
    !selections.morphology.every(
      (finding) => typeof finding === 'string' && MORPHOLOGY_IDS.has(finding)
    ) ||
    !isValidAge(selections.age) ||
    !(
      selections.onset === null ||
      (typeof selections.onset === 'string' && ONSET_IDS.has(selections.onset))
    ) ||
    typeof selections.skipped !== 'boolean'
  ) {
    return undefined;
  }
  return selections as unknown as VisualDxDiagnosticFlowSelections;
};

export const toStaticStatus = (status: string): 'fetching' | 'completed' | 'error' => {
  if (status === 'error' || status === 'cancelled') return 'error';
  if (status === 'complete') return 'completed';
  return 'fetching';
};

export const isInProgress = (status: string): boolean =>
  !['complete', 'error', 'cancelled'].includes(status);
